import { PolygonPointArray } from '@services/concern-detections-service';

type Point = [number, number];

interface HairlineSample {
  x: number;
  detectedY: number;
  idealY: number;
  recessionDepth: number;
}

interface SampleRun {
  recession: boolean;
  samples: HairlineSample[];
}

interface CurveTails {
  detectedLeft: Point[];
  detectedRight: Point[];
  idealLeft: Point[];
  idealRight: Point[];
}

export interface HairlineRecessionGeometry {
  lobes: number[][][];
  curveSegments: number[][][];
}

const DEFAULT_SAMPLE_COUNT = 200;
const MIN_RECESSION_DEPTH_PX = 1;

export function computeHairlineRecession(
  detectedHairline: PolygonPointArray | undefined,
  idealHairline: PolygonPointArray | undefined,
  sampleCount: number = DEFAULT_SAMPLE_COUNT,
): HairlineRecessionGeometry {
  const detected = toLeftToRightPoints(detectedHairline);
  const ideal = toLeftToRightPoints(idealHairline);
  if (!detected || !ideal) {
    return {
      lobes: [],
      curveSegments: [detected, ideal].filter((curve: Point[] | null): curve is Point[] => !!curve),
    };
  }

  const startX = Math.max(detected[0][0], ideal[0][0]);
  const endX = Math.min(detected[detected.length - 1][0], ideal[ideal.length - 1][0]);
  if (endX <= startX) return { lobes: [], curveSegments: [detected, ideal] };

  const samples = buildSamples(detected, ideal, startX, endX, Math.max(2, sampleCount));
  const runs = splitIntoRuns(samples)
    .map((run: SampleRun): SampleRun => ({ ...run, recession: run.recession && isDrawableLobe(run.samples) }));
  const tails: CurveTails = {
    detectedLeft: detected.filter((point: Point): boolean => point[0] < startX),
    detectedRight: detected.filter((point: Point): boolean => point[0] > endX),
    idealLeft: ideal.filter((point: Point): boolean => point[0] < startX),
    idealRight: ideal.filter((point: Point): boolean => point[0] > endX),
  };

  const lobes: number[][][] = [];
  const curveSegments: number[][][] = [];
  runs.forEach((run: SampleRun, index: number): void => {
    const isFirst = index === 0;
    const isLast = index === runs.length - 1;
    if (run.recession) {
      lobes.push(toLobePolygon(run.samples, tails, isFirst, isLast));
      return;
    }
    curveSegments.push(...toCurveSegments(run.samples, tails, isFirst, isLast));
  });
  return { lobes, curveSegments };
}

function toLeftToRightPoints(points: PolygonPointArray | undefined): Point[] | null {
  const parsed = (points ?? [])
    .map(parsePoint)
    .filter((point: Point | null): point is Point => !!point);
  if (parsed.length < 2) return null;
  return parsed[0][0] <= parsed[parsed.length - 1][0] ? parsed : parsed.reverse();
}

function parsePoint(point: string | number[]): Point | null {
  const [x, y] = typeof point === 'string' ? point.split(',').map(Number) : point;
  return Number.isFinite(x) && Number.isFinite(y) ? [x, y] : null;
}

function buildSamples(
  detected: Point[],
  ideal: Point[],
  startX: number,
  endX: number,
  sampleCount: number,
): HairlineSample[] {
  const step = (endX - startX) / (sampleCount - 1);
  return Array.from({ length: sampleCount }, (unused: unknown, index: number): HairlineSample => {
    const x = startX + step * index;
    const detectedY = yAtX(detected, x);
    const idealY = yAtX(ideal, x);
    return { x, detectedY, idealY, recessionDepth: idealY - detectedY };
  });
}

function yAtX(points: Point[], x: number): number {
  for (let index = 0; index < points.length - 1; index += 1) {
    const [x1, y1] = points[index];
    const [x2, y2] = points[index + 1];
    const spansX = (x1 <= x && x <= x2) || (x2 <= x && x <= x1);
    if (!spansX) continue;
    if (x1 === x2) return (y1 + y2) / 2;
    return y1 + (((x - x1) / (x2 - x1)) * (y2 - y1));
  }
  return x <= points[0][0] ? points[0][1] : points[points.length - 1][1];
}

function splitIntoRuns(samples: HairlineSample[]): SampleRun[] {
  const runs: SampleRun[] = [];
  let current: HairlineSample[] = [samples[0]];
  let recession = samples[0].recessionDepth > 0;
  for (let index = 1; index < samples.length; index += 1) {
    const sample = samples[index];
    const inRecession = sample.recessionDepth > 0;
    if (inRecession !== recession) {
      const crossing = crossingSample(samples[index - 1], sample);
      current.push(crossing);
      runs.push({ recession, samples: current });
      current = [crossing];
      recession = inRecession;
    }
    current.push(sample);
  }
  runs.push({ recession, samples: current });
  return runs;
}

function crossingSample(a: HairlineSample, b: HairlineSample): HairlineSample {
  const t = a.recessionDepth / (a.recessionDepth - b.recessionDepth);
  const x = a.x + ((b.x - a.x) * t);
  const y = a.detectedY + ((b.detectedY - a.detectedY) * t);
  return { x, detectedY: y, idealY: y, recessionDepth: 0 };
}

function isDrawableLobe(lobe: HairlineSample[]): boolean {
  return lobe.length >= 3
    && lobe.some((sample: HairlineSample): boolean => sample.recessionDepth >= MIN_RECESSION_DEPTH_PX);
}

function toLobePolygon(
  samples: HairlineSample[],
  tails: CurveTails,
  isFirst: boolean,
  isLast: boolean,
): number[][] {
  let detectedEdge = samples.map((sample: HairlineSample): number[] => [sample.x, sample.detectedY]);
  let idealEdge = [...samples]
    .reverse()
    .map((sample: HairlineSample): number[] => [sample.x, sample.idealY]);
  if (isFirst) {
    detectedEdge = [...tails.detectedLeft, ...detectedEdge];
    idealEdge = [...idealEdge, ...[...tails.idealLeft].reverse()];
  }
  if (isLast) {
    detectedEdge = [...detectedEdge, ...tails.detectedRight];
    idealEdge = [...[...tails.idealRight].reverse(), ...idealEdge];
  }
  return [...detectedEdge, ...idealEdge];
}

function toCurveSegments(
  samples: HairlineSample[],
  tails: CurveTails,
  isFirst: boolean,
  isLast: boolean,
): number[][][] {
  let detectedSegment = samples.map((sample: HairlineSample): number[] => [sample.x, sample.detectedY]);
  let idealSegment = samples.map((sample: HairlineSample): number[] => [sample.x, sample.idealY]);
  if (isFirst) {
    detectedSegment = [...tails.detectedLeft, ...detectedSegment];
    idealSegment = [...tails.idealLeft, ...idealSegment];
  }
  if (isLast) {
    detectedSegment = [...detectedSegment, ...tails.detectedRight];
    idealSegment = [...idealSegment, ...tails.idealRight];
  }
  return [detectedSegment, idealSegment]
    .filter((segment: number[][]): boolean => segment.length >= 2);
}
